import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getBusinesses, getClassifieds, getContests, getSocialPosts } from "../lib/api";
import LoyaltyBadge from "../components/LoyaltyBadge";
import { DailyDeal, PetOfWeek } from "../components/HomeWidgets";
import { SectionHeading } from "../components/ArticleCard";
import { Store, Tag, Trophy, PawPrint, Flame, Star, Phone, ShoppingBag, Facebook, Sparkles } from "lucide-react";

const QUICK = [
  { to: "/loyalty", label: "Loyalty Club", icon: Sparkles, desc: "Member deals & subscriber perks" },
  { to: "/directory", label: "Business Directory", icon: Store, desc: "Local businesses & sponsors" },
  { to: "/classifieds", label: "Classifieds", icon: Tag, desc: "Buy, sell & hire locally" },
  { to: "/contests", label: "Contests", icon: Trophy, desc: "Win local prizes" },
  { to: "/pets", label: "Adoptable Pets", icon: PawPrint, desc: "Meet rescue dogs & cats" },
];

export default function Marketplace() {
  const [businesses, setBusinesses] = useState([]);
  const [classifieds, setClassifieds] = useState([]);
  const [contests, setContests] = useState([]);
  const [social, setSocial] = useState([]);

  useEffect(() => {
    getBusinesses().then((b) => setBusinesses(b.slice(0, 3))).catch(() => {});
    getClassifieds().then((c) => setClassifieds(c.slice(0, 4))).catch(() => {});
    getContests().then(setContests).catch(() => {});
    getSocialPosts().then((p) => setSocial(p.filter((x) => x.sponsored).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="paper-texture min-h-screen">
      <div className="border-b border-[var(--paper-border)] bg-[var(--navy)]">
        <div className="mx-auto max-w-7xl px-4 py-12">
          <p className="eyebrow flex items-center gap-2 text-[var(--gold)]"><ShoppingBag size={14} /> Shop Local · Amelia Island & Nassau County</p>
          <h1 className="font-display text-4xl font-extrabold text-white sm:text-5xl">The Marketplace</h1>
          <p className="font-serif-body mt-2 max-w-2xl text-white/80">Everything local in one place — today's best deal, our business directory, classifieds, contests, adoptable pets and posts from our advertising partners.</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10">
        {/* Quick links */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          {QUICK.map((q) => (
            <Link key={q.to} to={q.to} data-testid={`mp-quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`} className="card-hover rounded-xl border border-[var(--paper-border)] bg-white p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[var(--gold)]/15 text-[var(--gold)]"><q.icon size={20} /></span>
              <p className="font-display mt-3 text-lg font-bold text-[var(--navy)]">{q.label}</p>
              <p className="font-ui text-xs text-slate-500">{q.desc}</p>
            </Link>
          ))}
        </div>

        <div className="mt-12 grid gap-10 lg:grid-cols-3">
          <div className="space-y-12 lg:col-span-2">
            {/* Daily Deal */}
            <section>
              <SectionHeading title="Today's Daily Deal" tagline="Claim it free in the Loyalty Club" to="/loyalty" />
              <DailyDeal />
            </section>

            {/* Business Directory */}
            <section>
              <SectionHeading title="Featured Businesses" tagline="Loyalty Club merchants first" to="/directory" />
              <div className="grid gap-5 sm:grid-cols-3">
                {businesses.map((b) => (
                  <Link key={b.id} to="/directory" data-testid={`mp-business-${b.id}`} className="card-hover overflow-hidden rounded-lg border border-[var(--paper-border)] bg-white">
                    {b.image && <div className="img-zoom relative h-28 overflow-hidden"><img src={b.image} alt={b.name} className="h-full w-full object-cover" />{b.loyalty && <LoyaltyBadge className="absolute left-2 top-2" />}</div>}
                    {!b.image && b.loyalty && <div className="px-3 pt-3"><LoyaltyBadge /></div>}
                    <div className="p-3">
                      <div className="flex items-center gap-1">
                        <span className="eyebrow text-[var(--gold)]">{b.category}</span>
                        {b.sponsored && <Star size={11} className="text-[var(--gold)]" />}
                      </div>
                      <p className="font-display text-base font-bold text-[var(--navy)]">{b.name}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            {/* Classifieds */}
            <section>
              <SectionHeading title="Latest Classifieds" tagline="Self-serve marketplace" to="/classifieds" />
              <div className="grid gap-4 sm:grid-cols-2">
                {classifieds.map((c) => (
                  <Link key={c.id} to="/classifieds" className="card-hover flex items-center justify-between gap-3 rounded-lg border border-[var(--paper-border)] bg-white p-4">
                    <div>
                      <span className="eyebrow text-[var(--gold)]">{c.category}</span>
                      <p className="font-display text-base font-bold text-[var(--navy)]">{c.title}</p>
                      <p className="font-ui flex items-center gap-1 text-xs text-slate-500"><Phone size={11} /> {c.name}</p>
                    </div>
                    {c.price && <span className="font-display shrink-0 text-lg font-bold text-[var(--navy)]">{c.price}</span>}
                  </Link>
                ))}
              </div>
            </section>

            {/* Contests */}
            {contests.length > 0 && (
              <section>
                <SectionHeading title="Enter to Win" tagline="Community contests & giveaways" to="/contests" />
                {contests.slice(0, 1).map((c) => (
                  <Link key={c.id} to="/contests" className="card-hover flex flex-col gap-4 overflow-hidden rounded-xl border border-[var(--paper-border)] bg-white sm:flex-row">
                    {c.image && <div className="img-zoom h-40 w-full overflow-hidden sm:h-auto sm:w-56 sm:shrink-0"><img src={c.image} alt={c.title} className="h-full w-full object-cover" /></div>}
                    <div className="flex flex-col justify-center p-5">
                      <span className="eyebrow flex items-center gap-1 text-[var(--gold)]"><Trophy size={12} /> Contest</span>
                      <p className="font-display text-xl font-bold text-[var(--navy)]">{c.title}</p>
                      <p className="font-serif-body mt-1 text-sm text-slate-600">{c.prize}</p>
                      <span className="font-ui mt-2 text-xs font-semibold uppercase tracking-wider text-[var(--gold)]">Enter now →</span>
                    </div>
                  </Link>
                ))}
              </section>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-8">
            <div>
              <h3 className="section-rule font-display pt-2 text-lg font-bold text-[var(--navy)]">Pet of the Week</h3>
              <div className="mt-4"><PetOfWeek /></div>
            </div>
            <div>
              <h3 className="section-rule flex items-center gap-2 font-display pt-2 text-lg font-bold text-[var(--navy)]"><Facebook size={16} className="text-[#1877F2]" /> Sponsored Social</h3>
              <div className="mt-4 space-y-4">
                {social.map((p) => (
                  <div key={p.id} className="rounded-lg border border-amber-200 bg-amber-50/60 p-4" data-testid={`mp-social-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-ui text-sm font-bold text-[var(--ink)]">{p.author}</p>
                      <span className="eyebrow rounded-sm bg-amber-100 px-1.5 py-0.5 text-[9px] text-amber-700">Co-Sponsored</span>
                    </div>
                    <p className="font-serif-body mt-1 line-clamp-3 text-sm text-slate-700">{p.text}</p>
                  </div>
                ))}
                {social.length === 0 && <p className="font-ui text-sm text-slate-400">No sponsored posts yet.</p>}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
